import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { FormProvider } from 'react-hook-form';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { FaInfoCircle } from 'react-icons/fa';
import { PermissionKeys } from 'store/types';
import { useWorkModeContext } from 'store/work-mode';
import { usePermissionsContext } from 'store/permissions';
import { getUserInfo } from 'utils/auth';
import { getSubdomain } from 'utils/helpers/routing';
import FormErrors from './FormErrors';
import FormButtons from './FormButtons';

interface IProps {
  title?: string;
  titleSpan?: number;
  titleOffset?: number;
  children?: React.ReactNode;
  methods: any;
  onSubmit?: any;
  btnLabel: string;
  submitTestId?: string;
  className?: string;
  primaryActionBtnType?: 'primary' | 'danger';
  showBackBtn?: boolean;
  onCancel?: () => void;
  onGoBack?: () => void;
  hideCancelBtn?: boolean;
  hideAllBtns?: boolean;
  areBtnsCentered?: boolean;
  isHorizontal?: boolean;
  isSubmitDisabled?: boolean;
  isUploading?: boolean;
  disabledBtnTooltip?: string;
  showGlobalError?: boolean;
  showFileApprovalText?: boolean;
  showNoFileApprovalText?: boolean;
  showPaidFileApprovalText?: boolean;
  onError?: (errors: any) => void;
  customError?: boolean;
  extraActionLabel?: string;
  setExitInterviewRef?: (toggle: boolean) => void;
  extraAction?: boolean;
}

/*
 * This is our custom form component, which is built on `react-bootstrap` Form and
 * wrapped with `FormProvider` from `react-hook-form`.
 */
const FormComponent: FC<IProps> = ({
  title,
  children,
  methods,
  onSubmit,
  extraActionLabel,
  btnLabel,
  submitTestId,
  primaryActionBtnType,
  onCancel,
  onGoBack,
  showBackBtn,
  hideCancelBtn,
  hideAllBtns,
  areBtnsCentered,
  isHorizontal,
  className = '',
  isSubmitDisabled,
  disabledBtnTooltip,
  showGlobalError,
  showFileApprovalText,
  showNoFileApprovalText,
  showPaidFileApprovalText,
  titleSpan = 10,
  titleOffset = 2,
  isUploading,
  onError,
  customError = false,
  setExitInterviewRef,
  extraAction,
}) => {
  const { t } = useTranslation();
  const { checkPermission } = usePermissionsContext();
  const [showGlobalFieldsError, setShowGlobalFieldsError] = useState(false);
  const { workMode } = useWorkModeContext();
  const { formState, watch } = methods;
  const { isSubmitting, errors, submitCount } = formState;
  const commonError = errors.errors_common;
  const errorRef = useRef<HTMLDivElement | null>(null);
  const fieldCategory = watch('category_id');
  const isOperator = getSubdomain() === 'operators';
  const userInfo = getUserInfo();
  const isEmployer = workMode === 'employer';
  const leavesObj: { [key: string]: string } = {
    label: `5. ${t('docWizard.step1.leaves')}`,
    value: '5',
  };
  const isLeaveTypeSelected =
    fieldCategory?.value?.toString() === leavesObj.value &&
    fieldCategory?.label?.toLowerCase() === leavesObj.label.toLowerCase();
  const canCreateDoc = !isOperator && checkPermission(PermissionKeys.CAN_CREATE_DOCUMENTS);
  const isLeavesModuleActive = userInfo?.featureLeavesIsActive;
  const showLeavesInfoText = isLeaveTypeSelected && canCreateDoc && isLeavesModuleActive;

  const scrollToError = useCallback(() => {
    errorRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }, []);

  useEffect(() => {
    if (!customError && Object.keys(errors).length) {
      setShowGlobalFieldsError(true);
      if (onError) {
        onError(errors);
      }
    } else {
      setShowGlobalFieldsError(false);
    }
    if (customError && commonError) {
      methods.clearErrors('errors_common');
    }
  }, [errors, commonError, methods, customError, onError]);

  useEffect(() => {
    // errors_common can appear outside of a submit attempt (e.g. a file upload
    // error inside a modal-hosted form) - scroll to it whenever it shows up,
    // not only after a failed submit. React Hook Form mutates `errors` in place,
    // so only the nested `commonError` reference actually changes on `setError` -
    // it must be a dependency on its own or this effect won't re-run.
    if (commonError || (submitCount > 0 && Object.keys(errors).length > 0)) {
      scrollToError();
    }
  }, [errors, commonError, submitCount, scrollToError]);

  return (
    <FormProvider {...methods}>
      {title && (
        <Row>
          <Col lg={{ span: titleSpan, offset: titleOffset }}>
            <h3 className="h5 mb-4 mt-2 text-center text-lg-left">{title}</h3>
          </Col>
        </Row>
      )}
      <Form
        noValidate
        onSubmit={methods.handleSubmit(onSubmit, () => {
          scrollToError();
        })}
        onChange={() => methods.clearErrors('errors_common')}
        className={className}
      >
        {/* Global form  errors (if any) */}
        {!customError && commonError && (
          <div ref={errorRef}>
            <FormErrors
              errors={commonError}
              onClose={methods.clearErrors}
            />
          </div>
        )}

        {/* Form elements */}
        {children}

        {/* Divider */}
        {isHorizontal && <hr />}

        {showFileApprovalText && (
          <Row>
            <Col xs={{ span: 'auto', offset: 2 }}>
              <p style={{ color: '#ef476f' }}>* {t('fileUploader.approveUploadedFile')}</p>
            </Col>
          </Row>
        )}

        {showNoFileApprovalText && (
          <Row>
            <Col xs={{ span: 'auto', offset: 2 }}>
              <p style={{ color: '#ef476f' }}>* {t('fileUploader.noNeedToApproveUploadedFile')}</p>
            </Col>
          </Row>
        )}

        {showPaidFileApprovalText && (
          <Row>
            <Col xs={{ span: 'auto', offset: 2 }}>
              <p style={{ color: '#ef476f' }}>* {t('fileUploader.approvePaidLeaveUploadedFile')}</p>
            </Col>
          </Row>
        )}

        {showLeavesInfoText && (
          <Row className="mb-2">
            <Col
              xs={{ span: 'auto', offset: 0 }}
              style={{ width: '100%' }}
            >
              <p
                style={{
                  color: '#ef476f',
                  paddingLeft: '38px',
                  maxWidth: '70%',
                  margin: '0 auto',
                  position: 'relative',
                }}
              >
                <FaInfoCircle
                  size={15}
                  style={{
                    color: '#ef476f',
                    position: 'absolute',
                    left: '0',
                    top: '0',
                    marginTop: '3px',
                    marginLeft: '18px',
                  }}
                />
                {isEmployer ? (
                  t('docWizard.step1.showLeavesInfoTextForEmployer')
                ) : (
                  <Trans
                    i18nKey="docWizard.step1.showLeavesInfoTextForEmployee"
                    // eslint-disable-next-line jsx-a11y/anchor-has-content
                    components={{
                      a: (
                        <Link
                          to="/my-leaves"
                          className="link"
                        />
                      ),
                    }}
                  />
                )}
              </p>
            </Col>
          </Row>
        )}
        {/* Form buttons - submit button with/without cancel option */}
        {!hideAllBtns && (
          <FormButtons
            btnLabel={btnLabel}
            submitTestId={submitTestId}
            primaryActionBtnType={primaryActionBtnType}
            hideCancelBtn={hideCancelBtn}
            onCancel={onCancel}
            onGoBack={onGoBack}
            showBackBtn={showBackBtn}
            isHorizontal={isHorizontal}
            areBtnsCentered={areBtnsCentered}
            isSubmitting={isSubmitting || isUploading}
            isSubmitDisabled={isSubmitDisabled}
            disabledBtnTooltip={disabledBtnTooltip}
            showGlobalFieldsError={showGlobalError && showGlobalFieldsError}
            extraActionLabel={extraActionLabel}
            setExitInterviewRef={setExitInterviewRef}
            extraAction={extraAction}
          />
        )}
      </Form>
    </FormProvider>
  );
};

export default FormComponent;
